import {
  Email as EmailIcon,
  LocalShipping as LocalShippingIcon,
  NoteAlt as NoteIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { ContactActivityType, CrmActivityDomain } from '@otw/models/leads/contacts';
import React, { useEffect, useState } from 'react';
import { CrmActivityDomainPillSlider } from './CrmActivityDomainPillSlider';

/**
 * Activity types that get logged through this dialog. Tasks have their own
 * (more elaborate) flow in {@link TaskFormDialog}.
 */
export type LoggableActivityType = Extract<
  ContactActivityType,
  'call' | 'sms' | 'email' | 'note' | 'shipping_repairs'
>;

interface ContactActivityLogDialogProps {
  open: boolean;
  type: LoggableActivityType | null;
  contactName: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (note: string, domain: CrmActivityDomain) => Promise<void> | void;
}

const ACTIVITY_META: Record<
  LoggableActivityType,
  {
    title: string;
    icon: React.ReactNode;
    placeholder: string;
    accent: 'primary' | 'success' | 'warning' | 'info' | 'secondary';
  }
> = {
  call: {
    title: 'Log a call',
    icon: <PhoneIcon />,
    placeholder: 'What was discussed on the call?',
    accent: 'success',
  },
  sms: {
    title: 'Log SMS',
    icon: <SmsIcon />,
    placeholder: 'Summarize the text exchange…',
    accent: 'info',
  },
  email: {
    title: 'Log an email',
    icon: <EmailIcon />,
    placeholder: 'Summarize the email…',
    accent: 'primary',
  },
  note: {
    title: 'Create a note',
    icon: <NoteIcon />,
    placeholder: 'Jot down a note about this contact…',
    accent: 'warning',
  },
  shipping_repairs: {
    title: 'Shipping & Repairs',
    icon: <LocalShippingIcon />,
    placeholder: 'Summarize the shipping or repairs update…',
    accent: 'secondary',
  },
};

const ContactActivityLogDialog: React.FC<ContactActivityLogDialogProps> = ({
  open,
  type,
  contactName,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const [note, setNote] = useState('');
  const [domain, setDomain] = useState<CrmActivityDomain>('sales');

  useEffect(() => {
    if (open) {
      setNote('');
      setDomain('sales');
    }
  }, [open, type]);

  if (!type) return null;
  const meta = ACTIVITY_META[type];
  const accent = theme.palette[meta.accent].main;

  const handleSubmit = async () => {
    if (!note.trim()) return;
    await onSubmit(note.trim(), domain);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
      disableEnforceFocus
      slotProps={{
        backdrop: { sx: { pointerEvents: 'none' } },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiDialog-container': { pointerEvents: 'none' },
        '& .MuiPaper-root': { pointerEvents: 'auto' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: alpha(accent, 0.15),
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {meta.icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {meta.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              For {contactName || 'this contact'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <CrmActivityDomainPillSlider value={domain} onChange={setDomain} />
          <TextField
            autoFocus
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            placeholder={meta.placeholder}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !note.trim()}
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactActivityLogDialog;
